import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import Navbar from "../components/Navbar";
import CodeSimulatorSection from "../components/CodeSimulatorSection";
import BootcampSection from "../components/BootcampSection";
import AIInterviewTeaser from "../components/AIInterviewTeaser";
import FAQSection from "../components/FAQSection";
import CTABanner from "../components/CTABanner";
import Footer from "../components/Footer";
import DynamicBadge from "../components/DynamicBadge";
import SEO from "../components/SEO";
import { getBootcampStatus } from "../lib/bootcamp";

import {
  Video,
  FileText,
  Trophy,
  Users,
  ChevronRight,
  Play,
  BookOpen,
  Code,
  Zap,
  Award,
  Target,
  ArrowRight,
  Rocket,
  Star,
  Brain,
  Plus,
  Terminal,
  Activity,
  Globe,
  Shield,
  Cpu,
  TrendingUp,
  CheckCircle2,
  Sparkles,
  GraduationCap,
  LineChart
} from "lucide-react";

import HeroBackground from "../components/HeroBackground";

const Home = () => {
  const monoFont = { fontFamily: "'JetBrains Mono', monospace" };
  const soraFont = { fontFamily: "'Sora', sans-serif" };
  const [bootcampStatus, setBootcampStatus] = useState(null);

  useEffect(() => {
    getBootcampStatus().then(data => setBootcampStatus(data));
  }, []);

  return (
    <div style={{ backgroundColor: '#0A0F1E', color: '#FFFFFF', ...soraFont }} className="min-h-screen overflow-x-hidden" data-testid="home-page">
      <Navbar />
      <SEO 
        title="Home"
        description="The all-in-one platform for mastering Python through interactive tutorials, line-by-line code simulations, and AI-powered interviews."
      />

      {/* ─── HERO SECTION ─── */}
      <section style={{ backgroundColor: '#0A0F1E' }} className="relative min-h-screen flex flex-col items-center justify-center pt-32 pb-20 overflow-hidden">
        
        <HeroBackground variant="home" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex flex-col items-center text-center">
          {/* Dynamic Eyebrow badge */}
          <DynamicBadge />

          {/* Headline */}
          <motion.h1 
            initial={{ opacity: 0, y: 24 }} 
            animate={{ opacity: 1, y: 0 }}
            style={monoFont}
            className="text-[clamp(2.5rem,8vw,5.5rem)] font-black leading-[1.05] text-white tracking-tighter mb-8 max-w-5xl uppercase"
          >
            Learn Python. <br />
            <span className="text-[#00E676] italic" style={{ textShadow: '0 0 15px rgba(0, 230, 118, 0.2)' }}>Build Real Skills.</span> <br />
            Get <span className="amber-underline">Hired.</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            style={soraFont}
            className="text-[#6B7280] text-[clamp(1rem,3vw,1.25rem)] max-w-2xl leading-relaxed mb-16"
          >
            The all-in-one platform for mastering Python through interactive tutorials, live simulations, and AI-powered interviews.
          </motion.p>

          {/* Product Cards Grid */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: BookOpen, t: "Free Tutorials", d: "Structured lessons from basics to advanced.", tag: "Free", color: "#00E676", path: "/tutorials" },
              { 
                icon: GraduationCap, 
                t: "AI/ML Bootcamp", 
                d: "Project-driven path to job readiness.", 
                tag: bootcampStatus?.status === 'enrolling' ? 'Enrolling' : 
                     bootcampStatus?.status === 'in_progress' ? 'Running' : 
                     bootcampStatus?.status === 'completed' ? 'Full' : 'Soon', 
                color: bootcampStatus?.status === 'enrolling' ? '#00E676' : '#F59E0B', 
                path: "/bootcamp" 
              },
              { icon: Terminal, t: "Code Simulator", d: "Visualize execution line-by-line.", tag: "Free", color: "#00E676", path: "/playground" },
              { icon: Brain, t: "AI Interview", d: "Ace interviews with AI feedback.", tag: "Soon", color: "#F59E0B", path: "/ai-interview" }
            ].map((card, i) => (
              <Link key={i} to={card.path} className="group h-full">
                <Card style={{ padding: '2.5rem' }} className="atm-card h-full">
                  <card.icon style={{ color: card.color, opacity: 0.05 }} className="absolute -bottom-8 -right-8 w-32 h-32 pointer-events-none" />
                  <div className="pulsing-dot" />
                  
                  <div className="flex items-start justify-between mb-8 relative z-10">
                    <div style={{ backgroundColor: `${card.color}11`, borderColor: `${card.color}22` }} className="p-3 border">
                      <card.icon style={{ color: card.color }} className="w-6 h-6" />
                    </div>
                    <span style={{ ...monoFont, backgroundColor: '#0A0F1E', borderColor: '#1F2937' }} className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 border text-[#6B7280]">{card.tag}</span>
                  </div>
                  <div className="relative z-10">
                    <h3 style={monoFont} className="text-lg font-black text-white mb-2 uppercase tracking-tight">{card.t}</h3>
                    <p style={soraFont} className="text-[#6B7280] text-xs leading-relaxed">{card.d}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {/* CTAs */}
          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto">
            <Link to="/tutorials" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="btn-outline w-full sm:w-auto">
                EXPLORE TUTORIALS →
              </Button>
            </Link>
            <Link to="/bootcamp" className="w-full sm:w-auto">
              <Button size="lg" className="btn-primary w-full sm:w-auto">
                {bootcampStatus?.status === 'enrolling' ? 'JOIN THE BOOTCAMP' : 'VIEW BOOTCAMP'}
              </Button>
            </Link>
          </motion.div>

          {/* Social Proof */}
          <div style={monoFont} className="mt-20 flex flex-row flex-wrap items-center justify-center gap-10 text-[#6B7280] text-[10px] font-black uppercase tracking-[0.2em]">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00E676]" /> 2.4k Students</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00E676]" /> 50+ Projects</span>
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00E676]" /> No Credit Card</span>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── FREE TUTORIALS SECTION ─── */}
      <section style={{ backgroundColor: '#0A0F1E' }} className="relative py-32 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 flex flex-col items-center">
          
          <motion.div className="text-center mb-24 max-w-3xl">
            <div style={{ borderColor: '#00E67633', backgroundColor: '#00E67611' }} className="inline-flex items-center gap-2 px-3 py-1.5 border mb-8">
              <BookOpen className="w-3.5 h-3.5 text-[#00E676]" />
              <span style={monoFont} className="text-[9px] font-black text-[#00E676] uppercase tracking-[0.3em]">Free Education</span>
            </div>
            <h2 style={monoFont} className="text-[clamp(2rem,5vw,4.5rem)] font-black text-white leading-[1.05] mb-8 uppercase tracking-tighter">
              Learn Python the <span className="text-[#00E676] italic">Professional Way</span>
            </h2>
            <p style={soraFont} className="text-[#6B7280] text-lg leading-relaxed max-w-2xl mx-auto">
              Skip the fluff. Get straight to building <span className="amber-underline">production-grade</span> applications with our curated roadmap.
            </p>
          </motion.div>

          {/* Editorial Mockup */}
          <motion.div className="w-full mb-24">
            <Card style={{ padding: '0', overflow: 'hidden' }} className="border-[#1F2937] atm-card">
              <div style={{ backgroundColor: '#111827', borderBottom: '1px solid #1F2937' }} className="flex items-center justify-between px-6 py-4 relative z-10">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-500/20" />
                  <div className="w-3 h-3 bg-yellow-500/20" />
                  <div className="w-3 h-3 bg-[#00E676]/20" />
                </div>
                <div style={{ backgroundColor: '#0A0F1E' }} className="hidden sm:flex flex-1 max-w-md mx-auto h-8 items-center px-4">
                  <span style={monoFont} className="text-[#6B7280] text-[10px] truncate uppercase tracking-widest">devhirelab.dev/masterclass</span>
                </div>
                <div className="pulsing-dot" />
              </div>

              <div className="flex flex-col lg:flex-row min-h-[600px] relative z-10">
                {/* Left Sidebar */}
                <div style={{ backgroundColor: '#111827', borderRight: '1px solid #1F2937' }} className="hidden lg:flex w-80 flex-col p-10">
                  <span style={monoFont} className="text-[10px] text-[#00E676] font-black uppercase tracking-[0.3em] mb-10">Course Roadmap</span>
                  <div className="space-y-8">
                    {["Syntax & Basics", "Control Flow", "Data Structures", "Functions", "OOP Mastery"].map((t, i) => (
                      <div key={i} className="flex items-start gap-5 group cursor-pointer">
                        <span style={monoFont} className={`text-[10px] font-black ${i < 3 ? 'text-[#00E676]' : 'text-[#6B7280]'}`}>0{i+1}</span>
                        <span style={monoFont} className={`text-xs font-black uppercase tracking-tight ${i < 3 ? 'text-white' : 'text-[#6B7280] group-hover:text-white transition-colors'}`}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Main Content */}
                <div style={{ backgroundColor: '#0A0F1E' }} className="flex-1 p-10 md:p-20 relative overflow-hidden">
                  <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#00E676 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
                  <div className="relative z-10 max-w-3xl">
                    <span style={monoFont} className="text-[#00E676] text-[10px] font-black mb-6 block uppercase tracking-[0.3em]">Module 05 · Production Grade</span>
                    <h3 style={monoFont} className="text-4xl md:text-5xl font-black text-white mb-10 leading-tight uppercase tracking-tight">Object Oriented <br /> Architecture</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                      <div className="space-y-6">
                        <p style={soraFont} className="text-[#6B7280] text-sm leading-relaxed">Object-oriented programming (OOP) is a paradigm centered around "objects" rather than "functions". It makes complex code modular and scalable.</p>
                        <div style={{ backgroundColor: '#111827', borderLeft: '3px solid #00E676' }} className="p-6 relative">
                          <p style={soraFont} className="text-xs text-[#00E676] font-medium leading-relaxed italic">"Think of classes as blueprints and objects as the actual physical entities."</p>
                          <div className="absolute top-2 right-2 w-1 h-1 bg-[#00E676] opacity-30" />
                        </div>
                      </div>
                      <div style={{ backgroundColor: '#111827', borderColor: '#1F2937' }} className="p-6 border font-mono text-[11px] leading-relaxed text-[#00E676] relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-b from-[#00E676]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                        <span className="text-white opacity-50"># Define a professional architecture</span><br/>
                        <span className="text-white">class</span> Developer:<br/>
                        &nbsp;&nbsp;<span className="text-white">def</span> __init__(self, name):<br/>
                        &nbsp;&nbsp;&nbsp;&nbsp;self.name = name<br/>
                        &nbsp;&nbsp;<span className="text-white">def</span> code(self):<br/>
                        &nbsp;&nbsp;&nbsp;&nbsp;return <span className="text-[#F59E0B]">"Writing Python..."</span><span className="animate-pulse">|</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          <Link to="/tutorials" className="w-full sm:w-auto">
            <Button size="lg" className="btn-primary w-full sm:w-auto h-20 px-16 text-xl">
              Start Learning Free →
            </Button>
          </Link>
        </div>
      </section>

      <div className="section-divider" />
      <CodeSimulatorSection />
      <div className="section-divider" />
      <BootcampSection />
      <div className="section-divider" />
      <AIInterviewTeaser />
      <div className="section-divider" />
      <FAQSection />
      <div className="section-divider" />
      <CTABanner />
      <Footer />
    </div>
  );
};

export default Home;
